import type { RouteLocationNormalizedLoaded } from "vue-router";

export const OBSERVABILITY_EVENT_NAMES = {
  navigationLandingViewed: "navigation.landing.viewed",
  navigationPageViewed: "navigation.page.viewed",
  // Fired once per map mount when the first real GPS fix lands, so a
  // journey's location is recorded at the START of play (not only at
  // experience.ended). Carries the auto-injected gps via dispatchUserEvent's
  // canonical-context enricher. Lets the analytics geo rollup plot map-only
  // and never-completed journeys.
  navigationLocationCaptured: "navigation.location.captured",
} as const;

type PageMetadata = {
  pageName: string;
  pageType: string;
  journeyStage: string;
  journeyStep: string;
};

const inferPageType = (routeName: string | null, path: string): string => {
  switch (routeName) {
    case "Register":
      return "registration";
    case "SignIn":
      return "sign_in";
    case "Enter Pin":
      return "enter_pin";
    case "Wallet":
      return "vault";
    case "Map":
      return "map";
    case "AR":
      return "ar_viewer";
    case "Scanner":
      return "scanner";
    case "Permissions":
      return "permissions";
    case "Prize":
      return "prize";
    case "Game":
      return "game";
    case "DeviceDetection":
      return "device_gate";
    case "CampaignPaused":
      return "campaign_paused";
    case "Custom":
      return "custom";
    case "Error":
      return "error";
    default:
      if (path.endsWith("/enter-pin")) return "enter_pin";
      if (path.endsWith("/sign-in")) return "sign_in";
      if (path.endsWith("/map")) return "map";
      if (path.endsWith("/vault")) return "vault";
      if (path.endsWith("/scanner")) return "scanner";
      if (path.endsWith("/ar-viewer")) return "ar_viewer";
      if (path.endsWith("/permissions")) return "permissions";
      return "custom";
  }
};

const inferJourneyStage = (pageType: string): string => {
  switch (pageType) {
    case "registration":
    case "sign_in":
    case "enter_pin":
      return "auth";
    case "permissions":
      return "permissions";
    case "map":
    case "scanner":
    case "ar_viewer":
      return "experience";
    case "prize":
    case "vault":
      return "reward";
    default:
      return "navigation";
  }
};

export const derivePageObservability = (
  route: Pick<RouteLocationNormalizedLoaded, "name" | "path"> | { name?: string | null; path: string }
): PageMetadata => {
  const pageName = typeof route.name === "string" ? route.name : "Unknown";
  const pageType = inferPageType(typeof route.name === "string" ? route.name : null, route.path);

  return {
    pageName,
    pageType,
    journeyStage: inferJourneyStage(pageType),
    journeyStep: pageType,
  };
};
